import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GRID_INFRA_CODES, GRID_INFRA_PREFIX, municipalityDetails, Strings } from '@app/shared/constants';
import { ErrorDialogComponent } from '@app/shared/dialog/error-dialog/error-dialog.component';
import { ExcelSheetName, PermissionCode, Tenant, UserStatus } from '@app/shared/enums';
import { IErrorDialogComponent } from '@app/shared/models/dialog/error-dialog';
import { RegionMunicipalitiesExpandResponse } from '@app/shared/models/municipality/Municipality';
import { NullableDate, NullableString } from '@app/shared/models/nullable';
import { User } from '@app/shared/models/user';
import { ApiService } from '@app/shared/services/api/api.service';
import { Excel, ExcelService } from '@app/shared/services/api/excel.service';
import { FeaturePermission, MunicipalityRestrictionsMapping, PermissionService } from '@app/shared/services/api/permission.service';
import { UserService } from '@app/shared/services/api/user.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { saveFile } from '@app/shared/utility';
import { cssVariables } from '@env/css-vars';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty as _isEmpty, isNil as _isNil, keys, map as _map, values, isNil } from 'lodash';
import * as momenttz from 'moment-timezone';
import { combineLatest, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class MunicipalityManagementApiService extends ApiService {
  constructor(
    public http: HttpClient,
    private _permissionService: PermissionService,
    private _excelService: ExcelService,
    private _loader: OverlayLoaderService,
    private translateService: TranslateService,
    private userService: UserService,
    private dialog: MatDialog
  ) {
    super(http);
  }

  public getMunicipalityReport(): Observable<any> {
    const fileName = `Kommunenuebersicht_${momenttz().format('DD-MM-YYYY')}.xlsx`;
    this._loader.isLoading = true;

    const selectQuery = `&$select=firstname,lastname,guid,status`;
    const filterQuery = `?$filter=Role/Code in ('SYSADMIN','PODSO') AND firstname ne null AND firstname ne '' AND lastname ne null AND lastname ne ''${selectQuery}`;

    return combineLatest([
      this.getMunicipalityRestrictionsExtendedInfo1(),
      this._permissionService.getAllBasicPremiumRestrictions(),
      this._permissionService.getFeaturePermissonsAllMunicipalities(),
      this.userService.getAll(filterQuery),
      this.getRegionMunicipalities(),
    ]).pipe(
      map(([report, pagePermissions, featurePermissions, users, regionMunicipalities]) => {
        try {
          const csvData = report.map((municipality: any) => {
            const basicFeatures = this.getBasicFeatures(municipality, pagePermissions);
            return {
              ...this.getMunicipalityData(municipality, regionMunicipalities),
              ...basicFeatures,
              ...this.getPremiumFeatures(pagePermissions, municipality),
              ...this.getAdditionalData(municipality),
            };
          });

          const externeAppHeaders = report.map(() => this.getExterneAppHeaders(featurePermissions));
          const externeAppValues = report.map((municipality: any) => this.getExterneAppValues(featurePermissions, municipality, users, regionMunicipalities));

          return [
            {
              fileName: fileName,
              sheetName: ExcelSheetName.Kommunenuebersicht,
              headers: keys(csvData[0]),
              data: _map(csvData, values),
              protectedColumns: [],
              templateCode: '',
              headerBgColor: cssVariables['--ep-primary-color'],
              secretPhrase: '',
            },
            {
              fileName: fileName,
              sheetName: ExcelSheetName.ExterneApp,
              headers: externeAppHeaders[0],
              data: externeAppValues,
              protectedColumns: [],
              templateCode: '',
              headerBgColor: cssVariables['--ep-primary-color'],
              secretPhrase: '',
            },
          ] as Excel[];
        } catch (error) {
          return [];
        }
      }),
      switchMap((reportData: Excel[]) => {
        return this._excelService.generateExterneAppExcel(reportData);
      }),
      tap((reportData) => {
        const blob = new Blob([reportData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveFile(blob, fileName);
      }),
      catchError((error) => {
        this.showServerError();
        return throwError(() => new Error(error));
      }),
      finalize(() => (this._loader.isLoading = false))
    );
  }

  private getMunicipalityRestrictionsExtendedInfo1(): Observable<any[]> {
    return this.http.get<any>(this.endpoint(`socrates/authz/api/views/Getextendedinfo1`));
  }

  private getRegionMunicipalities(): Observable<RegionMunicipalitiesExpandResponse[]> {
    return this.http.get<RegionMunicipalitiesExpandResponse[]>(
      this.endpoint(
        `socrates/authz/api/municipalities?$select=Guid&$expand=RegionMunicipalities($select=RegionId,Guid;$expand=Region($select=Name;$expand=RegionType($select=Code)))`
      )
    );
  }

  private getMunicipalityData(municipality: any, regionMunicipalities: RegionMunicipalitiesExpandResponse[]) {
    return {
      ['AGS (Amtlicher Gemeindeschlüssel)']: municipality.MunicipalityKey,
      Gemeindename: municipality.MunicipalityName,
      PLZ: municipality.ZipCode,
      Gemeindeverband: this.getSelectedRegion(regionMunicipalities, municipality),
      IstDeaktiviert: municipality.IsDeactivated,
      ['Primärer KAM']: municipality.PrimaryKAM,
      Region: municipality.Region,
    };
  }

  getSelectedRegion(RegionMunicipalities: RegionMunicipalitiesExpandResponse[], municipality: any): string {
    const selectedMunicipality = RegionMunicipalities.find(
      (regionMunicipality) => regionMunicipality.guid.trim().toLowerCase() === municipality.MunicipalityId.trim().toLowerCase()
    );

    if (selectedMunicipality?.regionMunicipalities) {
      const adminAreaRegion = selectedMunicipality.regionMunicipalities
        .filter((regionMunicipality) => regionMunicipality.region.regionType.code === 'AM')
        .map((regionMunicipality) => regionMunicipality.region.name)
        .join(', ');

      return adminAreaRegion.length > 0 ? adminAreaRegion : Strings.unassignedRegion;
    }

    return '';
  }

  private getBasicFeatures(municipality: any, pagePermissions: FeaturePermission[]): { [featureName: string]: 'True' | 'False' } {
    const basicFeatures = this.mapDisplayableFeatures(pagePermissions);

    return basicFeatures.reduce((acc, feature) => {
      const isRestricted = this.isRestrictedFeature(feature.code);
      const canAccess = !isRestricted || this.isBayernwerkTenant();

      if (canAccess) {
        acc[feature.name] = this.getBasicFeatureStatus(pagePermissions, feature.code, municipality);
      }

      return acc;
    }, {} as { [featureName: string]: 'True' | 'False' });
  }

  private mapDisplayableFeatures(pagePermissions: FeaturePermission[]): { name: string; code: string }[] {
    return pagePermissions
      .filter((p) => !p.isPremium && p.code !== 'GRID_INFRA')
      .map((p) => {
        const isGridInfra = GRID_INFRA_CODES.has(p.code);
        return {
          name: isGridInfra ? `${GRID_INFRA_PREFIX}${p.name}` : p.name,
          code: p.code,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private getBasicFeatureStatus(pagePermissions: FeaturePermission[], code: string, municipality: any): 'True' | 'False' {
    const permission = pagePermissions.find((x) => x.code === code);
    if (!permission?.isActive) {
      return 'False';
    }

    const restriction = permission.municipalityRestrictions?.find((x) => x.municipalityId === municipality.MunicipalityId.toLowerCase());

    if (!municipality.MunicipalityId || !restriction) {
      return 'True';
    }

    return 'False';
  }

  private isBayernwerkTenant(): boolean {
    return environment.tenant?.trim().toLowerCase() === Tenant.BAYERNWERK;
  }

  private isRestrictedFeature(featureCode: string): boolean {
    return featureCode === PermissionCode.SOCKET_SERVICE || featureCode === PermissionCode.STREETLIGHT_PLANNER;
  }

  private getPremiumFeatures(pagePermissions: FeaturePermission[], municipality: any): { [key: string]: any } {
    let results: { [key: string]: any } = {};
    try {
      const premiumFeatures = pagePermissions
        .filter((a: FeaturePermission) => a.isPremium)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((y: FeaturePermission) => {
          return {
            guid: y.guid,
            name: y.name,
            code: y.code,
            isActive: y.isActive,
            municipalityRestrictions: y.municipalityRestrictions,
          };
        });
      premiumFeatures.forEach((feature: { [key: string]: any }) => {
        const featureCode = Object.keys(municipality)
          .map((municipalityFeature) => {
            if (municipalityFeature === feature.code) {
              return feature.code;
            }
          })
          .filter((feature) => feature)[0] as string;

        if (feature.isActive) {
          results[feature.name] =
            (feature?.municipalityRestrictions?.length ?? 0) > 0
              ? this.getPremiumFeatureStatus(feature?.municipalityRestrictions as MunicipalityRestrictionsMapping[], municipality, featureCode)
              : 'True';
        } else {
          results[feature.name] = 'False';
        }
      });
    } catch (error) {}
    return results;
  }

  private getPremiumFeatureStatus(teaserFeature: MunicipalityRestrictionsMapping[], municipality: any, code: string): string {
    const municipalityRestriction = teaserFeature.find((restriction) => restriction.municipalityId === municipality.MunicipalityId.toLowerCase());

    if (!municipalityRestriction || municipalityRestriction.isTeaser) {
      switch (code) {
        case PermissionCode.ENERGY_MONITOR:
          if (
            !_isNil(municipality.EnergyMonitorUrl) &&
            !_isEmpty(municipality.EnergyMonitorUrl) &&
            municipality.EnergyMonitorUrl.replace(/\s/g, '').length > 0
          ) {
            return municipality.EnergyMonitorUrl;
          }
          break;
        case PermissionCode.CO2_BALANCE_SHEET_LINK:
          if (!_isNil(municipality.ClimateNaviUrl) && !_isEmpty(municipality.ClimateNaviUrl) && municipality.ClimateNaviUrl.replace(/\s/g, '').length > 0) {
            return municipality.ClimateNaviUrl;
          }
          break;
        case PermissionCode.LOCAL_ELECTRICITY_MARKET_LINK:
          if (
            !_isNil(municipality.LocalElectricityMarketUrl) &&
            !_isEmpty(municipality.LocalElectricityMarketUrl) &&
            municipality.LocalElectricityMarketUrl.replace(/\s/g, '').length > 0
          ) {
            return municipality.LocalElectricityMarketUrl;
          }
          break;
        case PermissionCode.ROOM_MONITORING:
          if (
            !_isNil(municipality.RoomMonitoringUrl) &&
            !_isEmpty(municipality.RoomMonitoringUrl) &&
            municipality.RoomMonitoringUrl.replace(/\s/g, '').length > 0
          ) {
            return municipality.RoomMonitoringUrl;
          }
          break;
        default:
          break;
      }
      return this.getPremiumStatus(municipalityRestriction as MunicipalityRestrictionsMapping);
    }

    return 'False';
  }

  private getPremiumStatus(municipalityRestriction: MunicipalityRestrictionsMapping): string {
    if (!municipalityRestriction) {
      return 'True';
    } else if (municipalityRestriction.isTeaser) {
      return 'Teaser';
    }
    return 'False';
  }

  private getExterneAppHeaders(featurePermissions: FeaturePermission[]): string[] {
    const columnHeaders: string[] = [];
    const translationModule = `module.userProfile.generalConfiguration.downloadExcelHeaders.`;

    columnHeaders.push(...municipalityDetails);

    const externeApps = featurePermissions.sort((a, b) => a.germanName.localeCompare(b.germanName));
    externeApps.forEach((feature: FeaturePermission) => {
      const externeAppNameHeader: string = feature.germanName;
      const modifyUserNameHeader: string = this.translateService.instant(translationModule + 'modifiedBy');
      const modfiedDateHeader: string = this.translateService.instant(translationModule + 'modifiedOn');
      columnHeaders.push(externeAppNameHeader, modifyUserNameHeader, modfiedDateHeader);
    });

    return columnHeaders;
  }

  private getExterneAppValues(
    featurePermissions: FeaturePermission[],
    municipality: any,
    users: User[],
    regionMunicipalities: RegionMunicipalitiesExpandResponse[]
  ): string[] {
    const columnValues: string[] = [];
    const translationModule = `module.userProfile.generalConfiguration.downloadExcelHeaders.`;

    const municipalityKey = municipality.MunicipalityKey;
    const municipalityName = municipality.MunicipalityName;
    const zipCode = municipality.ZipCode;
    const administrationCollectivity = this.getSelectedRegion(regionMunicipalities, municipality);
    const isDeactivated = municipality.IsDeactivated;
    const primaryKAM = municipality.PrimaryKAM;
    const region = municipality.Region;

    columnValues.push(municipalityKey, municipalityName, zipCode, administrationCollectivity, isDeactivated, primaryKAM, region);

    const externeApps = featurePermissions.sort((a, b) => a.germanName.localeCompare(b.germanName));
    externeApps.forEach((feature: FeaturePermission) => {
      try {
        const lastModified = this.getLastModified(feature, feature.municipalityRestrictions, municipality.MunicipalityId);
        const userDetails = users.find((details) => details.guid === lastModified.user);
        const featureModifyDate = momenttz.tz(lastModified.date, 'Europe/Berlin').format('DD.MM.YYYY hh:mm');
        const externeAppStatus =
          feature.municipalityRestrictions?.length > 0
            ? this.getExterneAppStatus(feature.municipalityRestrictions, feature.isActive, municipality.MunicipalityId)
            : 'True';
        let userName = '';

        if (userDetails?.status === UserStatus.active) {
          userName = userDetails?.firstname + ' ' + userDetails?.lastname;
        } else {
          userName = this.translateService.instant(translationModule + 'inactiveUser');
        }
        columnValues.push(externeAppStatus, userName, featureModifyDate);
      } catch (error) {
        columnValues.push('ERROR', '', '');
      }
    });

    return columnValues;
  }

  private getExterneAppStatus(municipalityRestrictions: MunicipalityRestrictionsMapping[], isActive: boolean | undefined, municipalityId: string): string {
    const restrictions = municipalityRestrictions.filter((f) => f.municipalityId?.toLowerCase() === municipalityId.toLowerCase() && !f.isDeleted);
    const hasRestrictions: boolean = restrictions?.length > 0;

    if (!hasRestrictions) {
      return 'True';
    }

    if (!restrictions[0].isTeaser || !isActive) {
      return 'False';
    } else {
      return 'Teaser';
    }
  }

  private getLastModified(
    featurePermission: FeaturePermission,
    municipalityRestrictions: MunicipalityRestrictionsMapping[],
    municipalityId: string
  ): { date: NullableDate; user?: NullableString } {
    if (municipalityRestrictions.length > 0) {
      const restrictionList = municipalityRestrictions.filter((f) => f.municipalityId?.toLowerCase() === municipalityId.toLowerCase());
      let modifiedList = [
        { date: isNil(featurePermission.modifyDate) ? featurePermission.creationDate : featurePermission.modifyDate, user: featurePermission.modifyUser },
        { date: restrictionList[0]?.creationDate, user: restrictionList[0]?.modifyUser },
        { date: restrictionList[0]?.modifyDate, user: restrictionList[0]?.modifyUser },
      ];
      modifiedList = modifiedList.filter((mod) => !!mod.date);
      return modifiedList.reduce((a, b) => ((a.date || new Date(1700, 1, 1)) > (b.date || new Date(1700, 1, 1)) ? a : b));
    } else {
      return { date: featurePermission.creationDate, user: featurePermission.creationUser };
    }
  }

  private getAdditionalData(municipality: any) {
    if (environment.tenant.trim().toLowerCase() === Tenant.BAYERNWERK) {
      return {
        CustomerCenter: municipality.CustomerCenter,
        IsInTestMode: municipality.IsInTestMode,
        WebApp: municipality.WebApp,
        WebAppPicture: municipality.WebAppPicture,
      };
    }
  }

  private showServerError(): void {
    this.dialog.open<ErrorDialogComponent, IErrorDialogComponent, unknown>(ErrorDialogComponent, {
      data: this.getServerErrorTranslation(),
      panelClass: ['ep-generic-dialog'],
      disableClose: true,
    });
  }

  private getServerErrorTranslation(): IErrorDialogComponent {
    const translationModule = `module.userProfile.generalConfiguration.errorScenario.serverError.`;
    return {
      title: this.translateService.instant(translationModule + 'title'),
      description: this.translateService.instant(translationModule + 'description'),
      buttonText: this.translateService.instant(translationModule + 'buttonText'),
    };
  }
}
